/* eslint-disable react-refresh/only-export-components */
import type { EstimateStatus } from '@/types/estimate';
import type { DynamicServicePricingConfig } from '@/types/pricebook';
import { fmt } from '@/components/organisms/sales/sales-document-helpers';
import { dynamicLaborBasis } from '@/lib/sales/dynamic-service-price';
import { formatHours } from '@/lib/sales/tmLine';
export {
  useDebounce,
  fmt,
  fmtFull,
  formatDate,
  formatDateTime,
} from '@/components/organisms/sales/sales-document-helpers';

// ─── Status config ────────────────────────────────────────────────────────────

export const STATUS_FILTERS: { value: EstimateStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'REVIEW', label: 'Review' },
  { value: 'SENT', label: 'Sent' },
  { value: 'VIEWED', label: 'Viewed' },
  { value: 'FOLLOW_UP', label: 'Follow Up' },
  { value: 'REVISED', label: 'Revised' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CONVERTED', label: 'Converted' },
];

export const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  REVIEW: 'bg-yellow-100 text-yellow-800',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-indigo-100 text-indigo-700',
  FOLLOW_UP: 'bg-warning/10 text-warning',
  APPROVED: 'bg-success/10 text-success',
  DECLINED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-warning/10 text-warning',
  REVISED: 'bg-purple-100 text-purple-700',
  CONVERTED: 'bg-teal-100 text-teal-700',
};

export const DOT_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-400',
  SENT: 'bg-blue-500',
  APPROVED: 'bg-success',
  DECLINED: 'bg-red-500',
  EXPIRED: 'bg-warning',
  REVIEW: 'bg-yellow-500',
  VIEWED: 'bg-indigo-500',
  FOLLOW_UP: 'bg-warning',
  REVISED: 'bg-purple-500',
  CONVERTED: 'bg-teal-500',
};

export function formatCurrency(amount: number | undefined | null): string {
  return fmt(amount);
}

/**
 * How a dynamically priced service arrives at its price, on the internal
 * estimate detail: the time it is priced from, and what that time is worth.
 *
 * READ OFF THE LINE'S CONFIGURATION, never its `laborRate`. A materialized
 * dynamic service keeps its rates in `dynamicPricingConfig.laborLines` and
 * carries no scalar rate at all, so printing that field stated "1 hrs @
 * $0.00/hr" under a service the same table was quoting at $1,699 — the estimate
 * telling its own operator that an hour of the work was worth nothing.
 *
 * Where the hours bill at more than one rate there is no "per hour" to print,
 * so the labor total is stated instead of a rate averaged out of tiers that
 * nothing actually charges. `laborRate` is still read for the one line that
 * genuinely has one: a flat service being priced dynamically by the document
 * (TM_OVERRIDE), which has no configuration to read.
 */
export function DynamicBasisText({
  line,
  pricingHidden,
}: {
  line: {
    estimatedHours?: number | null;
    laborRate?: number | null;
    dynamicPricingConfig?: DynamicServicePricingConfig | null;
  };
  pricingHidden: boolean;
}) {
  const basis = dynamicLaborBasis(line.dynamicPricingConfig);
  const hours = basis?.hours ?? line.estimatedHours ?? 0;
  const hoursText = `${formatHours(hours)} ${hours === 1 ? 'hr' : 'hrs'}`;
  // A rate the line can actually stand behind: the configuration's, where every
  // hour bills at one figure, and otherwise the scalar an unconfigured line
  // carries. Zero is not a rate — it is the field being empty.
  const rate = basis ? basis.hourlyRate : (line.laborRate ?? 0) || null;

  return (
    <p className="text-xs text-muted-foreground">
      Dynamic · {hoursText}
      {pricingHidden
        ? ''
        : rate != null
          ? ` @ ${fmt(rate)}/hr`
          : basis
            ? ` · ${fmt(basis.amount)} labor`
            : ''}
    </p>
  );
}
